/* ============================================================================
   Page-shell behaviour: the "on this page" rail, the FAQ accordion, and the
   wishlist grid. Loaded only by the page sections that need it.

   Every element here is progressive enhancement. The server always ships the
   full, readable content; JS only reorganises it. If this file fails to load,
   each page degrades to the plain prose it rendered before — no blank regions,
   no hidden answers.
   ========================================================================== */

const SLUG_STRIP = /[^a-z0-9]+/g;

function slugify(text, fallback) {
  const slug = String(text || '')
    .toLowerCase()
    .replace(SLUG_STRIP, '-')
    .replace(/^-+|-+$/g, '');
  return slug || fallback;
}

/* -- <ryvt-page-rail> -------------------------------------------------------
   Builds a sticky index from the H2s already present in the page body, so the
   rail costs the merchant nothing to author and cannot drift from the content.
   Stays hidden below the 3-heading threshold — a one- or two-item index is
   noise, and short pages (Contact, Track Order) have nothing to index. */
if (!customElements.get('ryvt-page-rail')) {
  class RyvtPageRail extends HTMLElement {
    connectedCallback() {
      const scope = this.closest('[data-ryvt-page-scope]');
      const source = scope && scope.querySelector('[data-ryvt-page-content]');
      if (!source) return;

      this.headings = Array.from(source.querySelectorAll('h2'));
      if (this.headings.length < 3) return;

      const list = document.createElement('ul');
      list.className = 'ryvt-rail__list';
      list.setAttribute('role', 'list');

      this.links = this.headings.map((heading, i) => {
        if (!heading.id) heading.id = slugify(heading.textContent, 'section-' + i);
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.className = 'ryvt-rail__link';
        a.href = '#' + heading.id;
        a.textContent = heading.textContent.trim();
        li.appendChild(a);
        list.appendChild(li);
        return a;
      });

      const label = document.createElement('p');
      label.className = 'ryvt-rail__label ryvt-label';
      label.textContent = this.getAttribute('data-label') || 'On this page';

      const nav = document.createElement('nav');
      nav.setAttribute('aria-label', label.textContent);
      nav.appendChild(label);
      nav.appendChild(list);

      this.replaceChildren(nav);
      this.hidden = false;
      this.observe();
    }

    observe() {
      /* Deterministic rather than IntersectionObserver-based: "current" is simply
         the last heading to have crossed a line 30% down the viewport. An IO band
         narrow enough to hold one heading leaves the previous one marked whenever
         no heading is inside it — which, between two far-apart sections, is most
         of the time. rAF-throttled, so it costs one rect read per frame at most. */
      const sync = () => {
        const line = window.innerHeight * 0.3;
        let current = 0;
        this.headings.forEach((h, i) => {
          if (h.getBoundingClientRect().top <= line) current = i;
        });
        this.links.forEach((link, j) => link.classList.toggle('is-current', j === current));
      };

      let ticking = false;
      this.onScroll = () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(() => {
          sync();
          ticking = false;
        });
      };

      window.addEventListener('scroll', this.onScroll, { passive: true });
      sync();
    }

    disconnectedCallback() {
      if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
    }
  }
  customElements.define('ryvt-page-rail', RyvtPageRail);
}

/* -- <ryvt-faq> -------------------------------------------------------------
   Transforms the authored h2/h3/p prose into categories of disclosure rows.
   The answers are never removed from the DOM — they are only collapsed — so the
   page stays fully indexable and the no-JS rendering is the complete FAQ. */
if (!customElements.get('ryvt-faq')) {
  class RyvtFaq extends HTMLElement {
    connectedCallback() {
      const source = this.querySelector('[data-ryvt-page-content]');
      if (!source) return;

      const nodes = Array.from(source.childNodes);
      const groups = [];
      let group = null;
      let row = null;

      nodes.forEach((node) => {
        const tag = node.nodeType === 1 ? node.tagName.toLowerCase() : '';
        if (tag === 'h2') {
          group = { heading: node, rows: [] };
          groups.push(group);
          row = null;
        } else if (tag === 'h3') {
          if (!group) {
            group = { heading: null, rows: [] };
            groups.push(group);
          }
          row = { question: node, answer: [] };
          group.rows.push(row);
        } else if (row && (node.nodeType === 1 || (node.textContent || '').trim())) {
          row.answer.push(node);
        }
      });

      /* Nothing recognisable to restructure — leave the prose exactly as served. */
      if (!groups.length || !groups.some((g) => g.rows.length)) return;

      const frag = document.createDocumentFragment();
      let rowIndex = 0;

      groups.forEach((g, gi) => {
        const section = document.createElement('section');
        section.className = 'ryvt-faq__group';

        if (g.heading) {
          if (!g.heading.id) g.heading.id = slugify(g.heading.textContent, 'faq-' + gi);
          g.heading.classList.add('ryvt-faq__group-heading');
          section.appendChild(g.heading);
        }

        /* Every row ships closed. Opening the first of each category cost six
           answers of height for an affordance the +/- already communicates, and
           the whole point of collapsing is that all 22 questions fit on screen
           at once. */
        g.rows.forEach((r) => {
          const id = 'faq-panel-' + rowIndex;
          const open = false;
          rowIndex += 1;

          const item = document.createElement('div');
          item.className = 'ryvt-faq__row';

          const btn = document.createElement('button');
          btn.type = 'button';
          btn.className = 'ryvt-faq__q';
          btn.id = id + '-btn';
          btn.setAttribute('aria-expanded', String(open));
          btn.setAttribute('aria-controls', id);
          btn.textContent = r.question.textContent.trim();

          const icon = document.createElement('span');
          icon.className = 'ryvt-faq__icon';
          icon.setAttribute('aria-hidden', 'true');
          btn.appendChild(icon);

          /* The authored h3 becomes the heading wrapper so the document outline
             survives the transform — screen readers still get a level-3 heading
             per question, with the button as its content. */
          const h = document.createElement('h3');
          h.className = 'ryvt-faq__qwrap';
          h.appendChild(btn);

          const panel = document.createElement('div');
          panel.className = 'ryvt-faq__panel ryvt-prose ryvt-body';
          panel.id = id;
          panel.setAttribute('role', 'region');
          panel.setAttribute('aria-labelledby', id + '-btn');
          panel.hidden = !open;
          r.answer.forEach((n) => panel.appendChild(n));

          btn.addEventListener('click', () => {
            const next = btn.getAttribute('aria-expanded') !== 'true';
            btn.setAttribute('aria-expanded', String(next));
            panel.hidden = !next;
          });

          item.appendChild(h);
          item.appendChild(panel);
          section.appendChild(item);
        });

        frag.appendChild(section);
      });

      source.replaceChildren(frag);
      source.classList.remove('ryvt-prose');
      source.classList.add('ryvt-faq__built');
    }
  }
  customElements.define('ryvt-faq', RyvtFaq);
}

/* -- <ryvt-wishlist> --------------------------------------------------------
   Saved items live client-side only (localStorage 'ryvt:wishlist', an array of
   product handles), so the grid has to hydrate in the browser. Each card is
   fetched through the Section Rendering API rather than rebuilt in JS, which
   keeps snippets/ryvt-product-card.liquid the single source of card markup. */
if (!customElements.get('ryvt-wishlist')) {
  class RyvtWishlist extends HTMLElement {
    connectedCallback() {
      this.grid = this.querySelector('[data-ryvt-wishlist-grid]');
      this.empty = this.querySelector('[data-ryvt-wishlist-empty]');
      this.busy = this.querySelector('[data-ryvt-wishlist-busy]');
      this.sectionId = this.getAttribute('data-card-section') || 'ryvt-wishlist-card';
      this.onChange = () => this.render();
      /* The runtime owns the wishlist store and publishes on its own bus, not on
         DOM events. It loads as a module in <head> so it has executed by the time
         section modules run — but subscribe defensively anyway, since a runtime
         failure should cost the live-update, not the initial render. */
      if (window.RYVT && window.RYVT.on) window.RYVT.on('ryvt:wishlist:change', this.onChange);
      this.render();
    }

    disconnectedCallback() {
      if (window.RYVT && window.RYVT.off) window.RYVT.off('ryvt:wishlist:change', this.onChange);
    }

    handles() {
      if (window.RYVT && window.RYVT.wishlist) {
        const list = window.RYVT.wishlist.list();
        return Array.isArray(list) ? list.filter(Boolean) : [];
      }
      try {
        const raw = window.localStorage.getItem('ryvt:wishlist');
        const list = raw ? JSON.parse(raw) : [];
        return Array.isArray(list) ? list.filter(Boolean) : [];
      } catch (e) {
        return [];
      }
    }

    async render() {
      const handles = this.handles();
      if (this.busy) this.busy.hidden = false;

      if (!handles.length) {
        if (this.grid) this.grid.replaceChildren();
        if (this.empty) this.empty.hidden = false;
        if (this.busy) this.busy.hidden = true;
        return;
      }
      if (this.empty) this.empty.hidden = true;

      const token = {};
      this.token = token;

      const cards = await Promise.all(
        handles.map(async (handle) => {
          try {
            const res = await fetch(
              '/products/' + encodeURIComponent(handle) + '?section_id=' + this.sectionId
            );
            if (!res.ok) return null;
            const html = await res.text();
            const doc = new DOMParser().parseFromString(html, 'text/html');
            return doc.querySelector('[data-ryvt-wishlist-card]');
          } catch (e) {
            /* A handle can 404 after a product is unpublished or renamed. Drop
               that card rather than failing the whole grid. */
            return null;
          }
        })
      );

      /* A newer render started while these were in flight — discard this pass. */
      if (this.token !== token) return;

      const found = cards.filter(Boolean);
      if (this.grid) this.grid.replaceChildren(...found);
      if (this.busy) this.busy.hidden = true;
      if (this.empty) this.empty.hidden = found.length > 0;
    }
  }
  customElements.define('ryvt-wishlist', RyvtWishlist);
}
